// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import {useCallback, useEffect, useState} from 'react';
import {i_zone_config, Proxy_type, t_account, t_order_users_state, t_orders,
    t_proxy_type_map, t_zone_group} from '../../../hola/pub/ui/pro/cp/orders';
// @ts-ignore
import {fetch} from '/util/fetch.js';
// @ts-ignore
import {get_json, set_json, clr} from '/util/storage.js';
import {get_active_group_with_fallback, has_free_plan} from '../ui/dashboard/utils';
import {i_user_info} from 'hola_app_context';
import etask from 'zetask';
import {perr} from 'hola_pub_util';
import {order_by_id} from '../../../toolip/pub/ui/dashboard/utils';
import {api} from './api';

export type t_usage_info = Record<string, number>;
export type t_countries = t_proxy_type_map<string[]>;
export interface t_free_zone_params {
    type: Proxy_type;
    country?: string;
}

interface i_fetch_data {
    is_loading: boolean;
    error?: string;
}
interface i_fetch_state {
    orders: i_fetch_data;
    create_zone_for_group: i_fetch_data;
    zone_update_config: i_fetch_data;
    zone_rename: i_fetch_data;
    zone_archive_toggle: i_fetch_data;
    zone_action: i_fetch_data;
    task_result_resource: i_fetch_data;
    create_new_task: i_fetch_data;
    zone_auto_renewal_toggle: i_fetch_data;
}
type i_fetch_key = keyof i_fetch_state;

export type t_dashboard_data = {
    account?: t_account;
    orders?: t_orders;
    zone_groups?: t_zone_group[];
    active_group?: t_zone_group;
    countries?: t_countries;
    free_zone_params?: t_free_zone_params;
    is_free_zone_creating: boolean;
    inited?: boolean;
    fetch_state: i_fetch_state;
};

export interface i_notification {
    _id: string;
    title: string;
    message: string;
    type: 'notification' | 'alert';
    target_users: string[];
    cta?: {
        label: string;
        url: string;
        is_external?: boolean;
    };
    icon?: string;
    attachments?: {
        url: string;
    }[];
    created_ts?: string;
    updated_ts?: string;
    is_active?: boolean;
    read?: boolean;
    read_ts?: string;
}

export type t_zone_action = 'start' | 'stop';

export type t_action_type =
    | {type: 'create_free_zone'; payload: {type: Proxy_type}}
    | {type: 'refresh_orders'}
    | {type: 'mark_orders_as_seen'; payload: {order_ids: string[]}}
    | {type: 'refresh_account_data'}
    | {type: 'mark_expired_orders_as_seen'; payload: {order_ids: string[]}}
    | {type: 'update_order_user_state'; payload: {
        order_id: string;
        user_state: Partial<t_order_users_state>
      }}
    | {type: 'set_free_zone_params'; payload: {params?: t_free_zone_params}}
    | {type: 'create_zone_for_group';
        payload: {group_id: string; type: Proxy_type; name?: string;}}
    | {type: 'zone_update_config';
          payload: {zone_id: string; config: Partial<i_zone_config>}}
    | {type: 'zone_rename'; payload: {zone_id: string; name: string;}}
    | {type: 'zone_action'; payload: {zone_id: string; action: t_zone_action;}}
    | {type: 'zone_archive_toggle';
        payload: {zone_id: string, archive: boolean}}
    | {type: 'get_task_by_session_id';
        payload: {session_id: string, zone_id: string}}
    | {type: 'get_zone_usage_stats';
        payload: {zone_id: string, start?: string; end?: string}}
    | {type: 'get_task_resource_by_task_resource_id';
        payload: {session_id: string, zone_id: string,
            task_resource_id: string}}
    | {type: 'create_new_task';
        payload: {task: string, geo_location: string, project: string,
            api_token: string}}
    | {type: 'zone_auto_renewal_toggle';
        payload: {zone_id: string, auto_renew: boolean}}

const every_5_minutes = 5*60*1000;

export const use_dashboard_data = (user: i_user_info)=>{
    const [info, set_info] = useState<t_dashboard_data>(()=>({
        free_zone_params: get_json('new_user_free_zone_params'),
        is_free_zone_creating: false,
        inited: false,
        fetch_state: {
            orders: {is_loading: false},
            create_zone_for_group: {is_loading: false},
            zone_update_config: {is_loading: false},
            zone_rename: {is_loading: false},
            zone_archive_toggle: {is_loading: false},
            zone_action: {is_loading: false},
            task_result_resource: {is_loading: false},
            create_new_task: {is_loading: false},
            zone_auto_renewal_toggle: {is_loading: false},
        },
        error_state: {},
    }));
    const set_fetch_state = useCallback(
    (key: i_fetch_key, is_loading: boolean, error?: string)=>{
        set_info(val=>({
            ...val,
            fetch_state: {...val.fetch_state, [key]: {is_loading, error}},
        }));
    }, []);
    const {free_zone_params, orders} = info;
    const show_free_zone_promo = free_zone_params || has_free_plan(orders);
    const refresh_orders = useCallback(()=>{
        set_fetch_state('orders', true);
        return Promise.all([
            fetch('/api/orders')
                .then(res=>set_info(val=>({...val, orders: res?.res}))),
            fetch('/api/groups')
                .then(res=>set_info(val=>({...val, zone_groups: res?.res}))),
        ])
        .then(()=>set_fetch_state('orders', false))
        .catch(err=>{
            console.error('Failed to refresh orders:', err);
            set_fetch_state('orders', false, 'Failed to load orders data');
        });
    }, []);
    useEffect(()=>{
        if (!user || !info.inited)
            return;
        const refresh_interval = setInterval(()=>
            dispatch({type: 'refresh_orders'}), every_5_minutes);
        return ()=>clearInterval(refresh_interval);
    }, [user, info.inited]);
    const dispatch = useCallback((action: t_action_type)=>{
        switch (action.type)
        {
            case 'refresh_orders':
                return refresh_orders();
            case 'mark_orders_as_seen':
                return api.update_orders_user_state(
                    action.payload.order_ids, {is_new: false});
            case 'mark_expired_orders_as_seen':
                return api.update_orders_user_state(
                    action.payload.order_ids, {expired_seen: true});
            case 'update_order_user_state': {
                const {order_id, user_state} = action.payload;
                set_info(val=>{
                    const order = order_by_id(val.orders, order_id);
                    if (!order)
                        return val;
                    const changed = {...order, user_state:
                        {...order.user_state, ...user_state}};
                    return {
                        ...val,
                        orders: {
                            ...val.orders,
                            [order.type]: val.orders[order.type]
                                .map(o=>o._id==order._id ? changed : o),
                        },
                    };
                });
                return etask(function*(){
                    yield api.update_orders_user_state([order_id], user_state);
                    dispatch({type: 'refresh_orders'});
                });
            }
            case 'set_free_zone_params': {
                const {params} = action.payload;
                set_json('new_user_free_zone_params', params);
                set_info(val=>({...val, free_zone_params: params}));
                return Promise.resolve();
            }
            case 'create_zone_for_group': {
                set_fetch_state('create_zone_for_group', true);
                return etask(function*(){
                    const {group_id, type, name} = action.payload;
                    set_fetch_state('create_zone_for_group', true);
                    const res = yield api.create_zone_for_group(
                        group_id, type, name);
                    set_fetch_state('create_zone_for_group', false);
                    dispatch({type: 'refresh_orders'});
                    return res;
                });
            }
            case 'get_task_by_session_id': {
                return etask(function*(){
                    const {session_id, zone_id} = action.payload;
                    const res = yield api.get_task_result_by_session_id(
                        zone_id, session_id);
                    set_fetch_state('create_zone_for_group', false);
                    return res;
                });
            }
            case 'get_task_resource_by_task_resource_id': {
                return etask(function*(){
                    const {session_id, zone_id, task_resource_id}
                        = action.payload;
                    set_fetch_state('task_result_resource', true);
                    const res = yield api.get_resource_from_task_result_by_id(
                        zone_id, session_id, task_resource_id);
                    set_fetch_state('task_result_resource', false);
                    return res;
                });
            }
            case 'get_zone_usage_stats': {
                return etask(function*(){
                    const {zone_id, start, end} = action.payload;
                    const res = yield api.get_zone_usage_stats(zone_id, start,
                        end);
                    set_fetch_state('create_zone_for_group', false);
                    return res;
                });
            }
            case 'zone_update_config': {
                return etask(function*(){
                    const {zone_id, config} = action.payload;
                    set_fetch_state('zone_update_config', true);
                    let res = yield api.update_zone_config(zone_id, config);
                    set_fetch_state('zone_update_config', false);
                    dispatch({type: 'refresh_orders'});
                    return res;
                });
            }
            case 'zone_rename': {
                return etask(function*(){
                    const {zone_id, name} = action.payload;
                    set_fetch_state('zone_rename', true);
                    yield api.update_zone(zone_id, {name}, action.type);
                    set_fetch_state('zone_rename', false);
                    dispatch({type: 'refresh_orders'});
                });
            }
            case 'zone_action': {
                return etask(function*(){
                    const {zone_id} = action.payload;
                    set_fetch_state('zone_action', true);
                    yield api.do_zone_action(zone_id, action.payload.action);
                    set_fetch_state('zone_action', false);
                    dispatch({type: 'refresh_orders'});
                });
            }
            case 'zone_archive_toggle': {
                return etask(function*(){
                    const {zone_id, archive} = action.payload;
                    set_fetch_state('zone_archive_toggle', true);
                    let res = yield api.archive_zone_toggle(zone_id, archive);
                    set_fetch_state('zone_archive_toggle', false);
                    dispatch({type: 'refresh_orders'});
                    return res;
                });
            }
            case 'zone_auto_renewal_toggle': {
                return etask(function*(){
                    const {zone_id, auto_renew} = action.payload;
                    set_fetch_state('zone_auto_renewal_toggle', true);
                    let res = yield api.update_auto_renewal(
                        zone_id,
                        auto_renew
                    );
                    set_fetch_state('zone_auto_renewal_toggle', false);
                    dispatch({type: 'refresh_orders'});
                    return res;
                });
            }
            case 'create_free_zone': {
                if (info.free_zone_params || has_free_plan(info?.orders))
                    return Promise.resolve();
                return dispatch({
                    type: 'set_free_zone_params',
                    payload: {params: {type: action.payload.type}},
                });
            }
            case 'refresh_account_data':
                return fetch('/api/account')
                    .then(res=>set_info(val=>({...val, account: res?.res})));
            case 'create_new_task': {
                set_fetch_state('create_new_task', true);
                return etask(function*(){
                    const {task, geo_location, project,
                        api_token} = action.payload;
                    set_fetch_state('create_new_task', true);
                    const res = yield api.create_new_task(
                        task, geo_location, project, api_token);
                    set_fetch_state('create_new_task', false);
                    dispatch({type: 'refresh_orders'});
                    return res;
                });
            }
        }
    }, [info.free_zone_params, info.orders]);
    useEffect(()=>{
        if (!user)
            return void set_info(val=>({...val, inited: true}));
        Promise.all([
            dispatch({type: 'refresh_orders'}),
            dispatch({type: 'refresh_account_data'}),
            fetch('/api/countries')
                .then(res=>set_info(val=>({...val, countries: res?.res}))),
        ]).finally(()=>set_info(val=>({...val, inited: true})));
    }, []);
    use_free_zone_creator({
        user, info, set_info,
        refresh_orders: ()=>dispatch({type: 'refresh_orders'})
    });
    const current_group = get_active_group_with_fallback(info.zone_groups);
    return {
        info: {...info, current_group},
        show_free_zone_promo, dispatch
    };
};

export type i_free_zone_creator_params = {
    user?: i_user_info;
    info?: t_dashboard_data;
    set_info: (value: React.SetStateAction<t_dashboard_data>)=>void;
    refresh_orders: ()=>void;
};
const use_free_zone_creator = (params: i_free_zone_creator_params)=>{
    const {user, info, set_info, refresh_orders} = params;
    useEffect(()=>{
        if (!user || !info.inited || !info.free_zone_params
            || info.is_free_zone_creating || has_free_plan(info.orders))
        {
            return;
        }
        etask(function*(){
            set_info(val=>({...val, is_free_zone_creating: true}));
            this.finally(()=>
                set_info(val=>({...val, is_free_zone_creating: false})));
            let zone = yield api.create_free_zone(info.free_zone_params.type);
            if (zone)
            {
                perr({id: 'toolip_free_zone_created'});
                clr('new_user_free_zone_params');
                set_info(val=>({
                    ...val,
                    free_zone_params: null,
                    is_free_zone_creating: false,
                }));
                refresh_orders();
            }
        });
    }, [user, info.inited, info.free_zone_params]);
};
